import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { CONFIG } from 'src/global-config';
import { Role } from 'src/enums/auth/role.enum';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';

import { usePathname } from '../hooks';

const IndexPage = lazy(() => import('src/pages/dashboard/dashboard'));

// Superadmin
const SuperAdminUserManagement = lazy(() => import('src/pages/superadmin/user/management'));
const SuperAdminUserCleanupPage = lazy(() => import('src/pages/superadmin/user/cleanup'));
const SuperAdminGroupManagement = lazy(() => import('src/pages/superadmin/group/management'));

// Swagger UI is currently dev-only due to known vulnerabilities in swagger-ui-react
const SuperadminSwaggerApiDocumentation =
  import.meta.env.DEV && CONFIG.swagger.enabled
    ? lazy(() => import('src/pages/superadmin/swagger-api-documentation'))
    : () => null;

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [{ element: <IndexPage />, index: true }],
  },
  {
    path: 'superadmin',
    element: (
      <AuthGuard>
        <RoleBasedGuard allowedRoles={[Role.SuperAdmin]}>{dashboardLayout()}</RoleBasedGuard>
      </AuthGuard>
    ),
    children: [
      ...(import.meta.env.DEV && CONFIG.swagger.enabled
        ? [{ path: 'swagger-api-documentation', element: <SuperadminSwaggerApiDocumentation /> }]
        : []),
      {
        path: 'user-management',
        element: <SuperAdminUserManagement />,
      },
      {
        path: 'user-cleanup',
        element: <SuperAdminUserCleanupPage />,
      },
      {
        path: 'group-management',
        element: <SuperAdminGroupManagement />,
      },
    ],
  },
];
